import {
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  MachinesListDataTypes,
  MachinesListSupportSessionsStatusTypes,
  MachinesListSupportSessionsZitadelRoleTypes,
} from "@common/types/machines-list-types";
import { SupportAgentTicket, TicketMachine } from "@src/types/support";
import { RootState } from "@src/reducers/store";
import { useI18N } from "@src/language/language-context";
import { getVPNSupportSessionsUseCase } from "@src/cloud-connect/vpn-manager/use-cases/vpn-get-support-session-use-case";
import { postVPNInterruptSupportSessionUseCase } from "@src/cloud-connect/vpn-manager/use-cases/vpn-post-interrupt-support-session-use-case";
import { postVPNCancelSupportSessionUseCase } from "@src/cloud-connect/vpn-manager/use-cases/vpn-post-cancel-support-session-use-case";
import { postVPNDenySupportSessionUseCase } from "@src/cloud-connect/vpn-manager/use-cases/vpn-post-deny-support-session-use-case";
import { postVPNAuthKeySupportSessionUseCase } from "@src/cloud-connect/vpn-manager/use-cases/vpn-post-generate-auth-key-support-session-use-case";
import { getVPNSingleUserUseCase } from "@src/cloud-connect/vpn-manager/use-cases/vpn-get-single-user-use-case";
import { handleActiveSupportSessionButtonClick } from "@src/components/molecules/machines-list-row/handle-active-support-session-button-click";
import {
  updateMachineInformationForRequestRemoteAccessDrawerModal,
  updateModalsCloseDrawerModal,
} from "@src/reducers/your-reducer";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { getSessionIdentityFlags } from "@src/cloud-connect/vpn-manager/utils/session-identity-flags";
import { useSSEUnavailable } from "@src/cloud-connect/sse/use-sse-unavailable";

const defaultApiHandlers = {
  interruptSupportSession: postVPNInterruptSupportSessionUseCase,
  cancelSupportSession: postVPNCancelSupportSessionUseCase,
  denySupportSession: postVPNDenySupportSessionUseCase,
  getAuthKey: postVPNAuthKeySupportSessionUseCase,
  getTechnicianById: getVPNSingleUserUseCase,
};

const normalizeSessionStatus = (
  status?: string,
): MachinesListSupportSessionsStatusTypes =>
  (status?.toUpperCase() ||
    MachinesListSupportSessionsStatusTypes.INITIAL) as MachinesListSupportSessionsStatusTypes;

const buildDefaultMachineData = ({
  namespace,
  machine,
}: {
  namespace: string;
  machine: TicketMachine | null | undefined;
}): MachinesListDataTypes => {
  return {
    id: "",
    namespace,
    gateway_id: machine?.gateway_id || "",
    source_id: machine?.machine_id || "",
    // TODO: machine_id temporarily holds the data source id, replace with machine_id
    name: machine?.machine_name || "",
    dsn: machine?.source_dsn || "",
    status: MachinesListSupportSessionsStatusTypes.INITIAL,
    requested_duration: "",
    remaining_duration: null,
    approved_duration: "",
  } as MachinesListDataTypes;
};

export const useTicketVpnManagerHook = ({
  ticket,
}: {
  ticket: SupportAgentTicket;
}) => {
  const dispatch = useDispatch();
  const i18n = useI18N();

  // Redux state
  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );
  const organization = useSelector(
    (state: RootState) => state.yourSlice.authContext.organization,
  );
  const showDisconnectSupportModal = useSelector(
    (state: RootState) =>
      state.yourSlice.modals.machinesCancelRequestDisconnectSupportModal
        .showModal,
  );

  // Local state
  const namespace = authContext?.customerNamespace || "";
  const ticketMachine = ticket.machine;
  const defaultMachineData = useMemo(
    () =>
      buildDefaultMachineData({
        namespace,
        machine: ticketMachine,
      }),
    [
      namespace,
      ticketMachine?.gateway_id,
      ticketMachine?.machine_id,
      ticketMachine?.machine_name,
      ticketMachine?.source_dsn,
    ],
  );
  const [machine, setMachine] =
    useState<MachinesListDataTypes>(defaultMachineData);
  const [displayName, setDisplayName] = useState<string>("");
  const isActionButtonsDisabled = useSSEUnavailable("service-vpn-manager");

  // Loading states
  const [stRequestClickLoading, setStRequestClickLoading] =
    useState<boolean>(false);
  const [otRequestClickLoading, setOtRequestClickLoading] =
    useState<boolean>(false);
  const [stActiveClickLoading, setStActiveClickLoading] =
    useState<boolean>(false);
  const [otActiveClickLoading, setOtActiveClickLoading] =
    useState<boolean>(false);
  const [
    confirmCancelRemoteAccessRequestLoading,
    setConfirmCancelRemoteAccessRequestLoading,
  ] = useState<boolean>(false);

  // Modal states
  const [showSTCancelModal, setShowSTCancelModal] = useState<boolean>(false);
  const [showOTCancelModal, setShowOTCancelModal] = useState<boolean>(false);

  // Permission checks
  const { showSTSupport, showOTSupport } = useMemo(() => {
    const allPermissions = authContext?.permissions ?? [];
    const hasManage = allPermissions.includes(
      MachinesListSupportSessionsZitadelRoleTypes.MANAGE_VPN,
    );
    const hasAccept = allPermissions.includes(
      MachinesListSupportSessionsZitadelRoleTypes.ACCEPT_VPN,
    );
    const hasRequest = allPermissions.includes(
      MachinesListSupportSessionsZitadelRoleTypes.REQUEST_VPN,
    );

    return {
      showSTSupport: hasManage || hasRequest,
      showOTSupport: hasManage || hasAccept,
    };
  }, [authContext?.permissions]);

  const {
    isCurrentUserSessionOwner,
    isCurrentUserSessionViewer,
    isCurrentUserSessionApprover,
    isManagerOwner,
    canRequestorCancelActiveInProgress,
    canApproverCancelActiveInProgress,
  } = getSessionIdentityFlags(machine, authContext);

  const texts = {
    // common status texts
    active: i18n("modules.machinesListRow.active"),
    request: i18n("modules.machinesListRow.request"),
    requested: i18n("modules.machinesListRow.requested"),
    incomingRequested: i18n("modules.machinesListRow.incomingRequested"),
    inProgressPrivate: i18n("modules.machinesListRow.inProgressPrivate"),
    inProgressSession: i18n("modules.machinesListRow.inProgressSession"),
    joinSession: i18n("modules.machinesListRow.joinSession"),
    inProgressPrivateTooltip: i18n(
      "modules.machinesListRow.inProgressPrivateTooltip",
    ),
    joinSessionTooltip: i18n("modules.machinesListRow.joinSessionTooltip"),
    viewing: i18n("modules.machinesListRow.viewing"),
    viewingTooltip: i18n("modules.machinesListRow.viewingTooltip"),
    sseUnavailableTooltip: i18n(
      "modules.sse.reconnectingActionsUnavailableTooltip",
    ),

    // cancel request cross modal
    cancelRequestModal: {
      title: i18n("modules.machinesCancelRequestCrossModal.title"),
      message: i18n("modules.machinesCancelRequestCrossModal.message"),
      confirmButton: i18n(
        "modules.machinesCancelRequestCrossModal.confirmButton",
      ),
      cancelButton: i18n(
        "modules.machinesCancelRequestCrossModal.cancelButton",
      ),
    },

    // disconnect support modal
    disconnectModal: {
      title: i18n("modules.machinesCancelRequestDisconnectSupportModal.title"),
      message: i18n(
        "modules.machinesCancelRequestDisconnectSupportModal.message",
      ),
      confirmButton: i18n(
        "modules.machinesCancelRequestDisconnectSupportModal.confirmButton",
      ),
      cancelButton: i18n(
        "modules.machinesCancelRequestDisconnectSupportModal.cancelButton",
      ),
    },
  };

  const sourceId = machine?.source_id || defaultMachineData.source_id;

  // Refresh session data on component mount and when sourceId changes
  const refreshSupportSession = useCallback(async (): Promise<
    MachinesListDataTypes | undefined
  > => {
    if (!sourceId) {
      return;
    }
    const sessionResponse = await getVPNSupportSessionsUseCase({
      source_id: sourceId,
    });

    if (!sessionResponse.success) {
      const fallbackMachine = {
        ...defaultMachineData,
        status: MachinesListSupportSessionsStatusTypes.INITIAL,
      };
      setMachine(fallbackMachine);
      return fallbackMachine;
    }
    const supportSessionData = sessionResponse.data?.[sourceId]?.[0];

    // if we dont get data for current source id, it fallbacks to initial state
    if (!supportSessionData) {
      const fallbackMachine = {
        ...defaultMachineData,
        status: MachinesListSupportSessionsStatusTypes.INITIAL,
      };
      setMachine(fallbackMachine);
      return fallbackMachine;
    }

    const updatedMachine = {
      ...supportSessionData,
      status: normalizeSessionStatus(supportSessionData.status),
      source_id: supportSessionData.source_id || sourceId,
      name: supportSessionData.name || defaultMachineData.name,
      dsn: supportSessionData.dsn || defaultMachineData.dsn,
      namespace: supportSessionData.namespace || namespace,
      requested_duration: supportSessionData.requested_duration || "",
      remaining_duration: supportSessionData.remaining_duration || null,
      approved_duration: supportSessionData.approved_duration || "",
    };

    setMachine(updatedMachine);
    return updatedMachine;
  }, [defaultMachineData, namespace, sourceId]);

  const lastEvent = useSelector(
    (state: RootState) =>
      state.sseSlice.streams?.["service-vpn-manager"]?.["lastEvent"],
  );

  const lastEventAt = useSelector(
    (state: RootState) =>
      state.sseSlice.streams?.["service-vpn-manager"]?.["lastEventAt"],
  );

  const events = useSelector(
    (state: RootState) =>
      state.sseSlice.streams?.["service-vpn-manager"]?.["events"] ?? {},
  );

  //onEvent
  useEffect(() => {
    const data = events?.["support_session"];
    if (data?.source_id !== sourceId || !data?.action) return;

    if (lastEvent == "support_session") refreshSupportSession();
  }, [lastEvent, lastEventAt, events, sourceId, refreshSupportSession]);

  // Initialize machine state
  useEffect(() => {
    setMachine(defaultMachineData);
    setDisplayName("");
  }, [defaultMachineData]);

  useEffect(() => {
    refreshSupportSession();
  }, [refreshSupportSession]);

  // Fetch technician display name
  const fetchTechnicianDisplayName = useCallback(async () => {
    if (!machine?.technician_user_id) return "";

    const response = await defaultApiHandlers.getTechnicianById(
      machine.technician_user_id,
      machine.technician_namespace,
    );

    if (!response.success) {
      errorSnackbar(response.error);
      return "";
    }

    const name = response.data?.display_name ?? "";
    setDisplayName(name);
    return name;
  }, [machine.technician_namespace, machine.technician_user_id]);

  const handleRequestButtonClick = async (
    e: MouseEvent | KeyboardEvent,
    userType: MachinesListSupportSessionsZitadelRoleTypes,
  ) => {
    if (isActionButtonsDisabled) return;

    const setLoading =
      userType === MachinesListSupportSessionsZitadelRoleTypes.REQUEST_VPN
        ? setStRequestClickLoading
        : setOtRequestClickLoading;

    setLoading(true);
    e.stopPropagation();

    // Fetch latest session data first
    const freshMachineData = await refreshSupportSession();
    if (
      !freshMachineData ||
      (userType === MachinesListSupportSessionsZitadelRoleTypes.REQUEST_VPN &&
        freshMachineData.status !==
          MachinesListSupportSessionsStatusTypes.REQUESTED &&
        !freshMachineData.isRequested &&
        !freshMachineData.gateway_id?.trim())
    ) {
      setLoading(false);
      return;
    }

    let userName = "";
    if (freshMachineData.status === "REQUESTED") {
      if (freshMachineData.technician_user_id) {
        const response = await defaultApiHandlers.getTechnicianById(
          freshMachineData.technician_user_id,
          freshMachineData.technician_namespace,
        );
        if (response.success) {
          userName = response.data?.display_name ?? "";
        }
      }
    }
    dispatch(
      updateMachineInformationForRequestRemoteAccessDrawerModal({
        sourceId: freshMachineData.source_id,
        id: freshMachineData.id,
        gatewayId: freshMachineData.gateway_id,
        sourceName: freshMachineData.name,
        companyName: organization,
        dsn: freshMachineData.dsn,
        namespace: freshMachineData.namespace,
        status: freshMachineData.status,
        requested_duration: freshMachineData.requested_duration,
        visibility: freshMachineData.visibility,
        display_name: userName,
        requestMessage: freshMachineData.message,
        userType,
      }),
    );

    dispatch(
      updateModalsCloseDrawerModal({
        showModal: true,
        id: "machinesRequestRemoteMachineAccessDrawerModal",
      }),
    );

    setLoading(false);
  };

  const openSTCancelRequest = (e: MouseEvent | KeyboardEvent) => {
    if (isActionButtonsDisabled) return;
    e.stopPropagation();
    setShowSTCancelModal(true);
  };

  const openOTCancelRequest = async (e: MouseEvent | KeyboardEvent) => {
    if (isActionButtonsDisabled) return;
    e.stopPropagation();
    await fetchTechnicianDisplayName();
    setShowOTCancelModal(true);
  };

  const closeOTCancelModal = (e: MouseEvent | KeyboardEvent) => {
    e.stopPropagation();
    setShowOTCancelModal(false);
  };

  const closeSTCancelModal = (e: MouseEvent | KeyboardEvent) => {
    e.stopPropagation();
    setShowSTCancelModal(false);
  };

  const closeDisconnectSupportModal = () => {
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: false,
        id: "machinesCancelRequestDisconnectSupportModal",
      }),
    );
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: false,
        id: "machinesRequestRemoteMachineAccessDrawerModal",
      }),
    );
  };

  const handleCancelOrInterrupt = async (
    action: "cancel" | "interrupt" | "deny",
    e: MouseEvent | KeyboardEvent,
  ) => {
    if (isActionButtonsDisabled) return;
    setConfirmCancelRemoteAccessRequestLoading(true);
    e.stopPropagation();

    if (!machine?.id || !machine?.namespace) {
      setShowOTCancelModal(false);
      setShowSTCancelModal(false);
      return;
    }

    const isActiveSession = ["ACCEPTED", "OPEN", "IN_PROGRESS"].includes(
      machine.status,
    );
    const apiAction =
      action === "cancel"
        ? isActiveSession
          ? defaultApiHandlers.interruptSupportSession
          : defaultApiHandlers.cancelSupportSession
        : isActiveSession
        ? defaultApiHandlers.interruptSupportSession
        : defaultApiHandlers.denySupportSession;

    try {
      const response = await apiAction({
        namespace: machine.namespace,
        id: machine.id,
      });

      if (!response.success) {
        errorSnackbar(response.error);
        return;
      }

      await refreshSupportSession();
    } catch (err) {
      console.error(`Error ${action}ing VPN request:`, err);
    } finally {
      setShowOTCancelModal(false);
      setShowSTCancelModal(false);
      setConfirmCancelRemoteAccessRequestLoading(false);
    }
  };

  const handleActiveButtonClick = async (
    userType: MachinesListSupportSessionsZitadelRoleTypes,
    e: MouseEvent | KeyboardEvent,
  ) => {
    if (isActionButtonsDisabled) return;

    const setLoading =
      userType === MachinesListSupportSessionsZitadelRoleTypes.REQUEST_VPN
        ? setStActiveClickLoading
        : setOtActiveClickLoading;

    if (!machine?.id || !machine?.namespace) {
      return;
    }

    await handleActiveSupportSessionButtonClick({
      e,
      setLoading,
      machine,
      isConnected: machine.connection_state === "CONNECTED",
      getAuthKey: defaultApiHandlers.getAuthKey,
    });
  };

  return {
    machine,

    showSTSupport: showSTSupport,
    showOTSupport: showOTSupport,

    showSTCancelModal,
    showOTCancelModal,
    showDisconnectSupportModal,

    stRequestClickLoading,
    otRequestClickLoading,
    stActiveClickLoading,
    otActiveClickLoading,
    confirmCancelRemoteAccessRequestLoading,

    displayName,
    texts,
    modalTexts: texts.cancelRequestModal,
    disconnectTexts: texts.disconnectModal,

    openSTCancelRequest,
    openOTCancelRequest,
    closeOTCancelModal,
    closeSTCancelModal,
    closeDisconnectSupportModal,
    confirmCancelRemoteAccessRequest: (e) =>
      handleCancelOrInterrupt("cancel", e),
    confirmInterruptRemoteAccessRequest: (e) =>
      handleCancelOrInterrupt("interrupt", e),
    handleRequestButtonClick,
    handleSTActiveButtonClick: (e) =>
      handleActiveButtonClick(
        MachinesListSupportSessionsZitadelRoleTypes.REQUEST_VPN,
        e,
      ),
    handleOTActiveButtonClick: (e) =>
      handleActiveButtonClick(
        MachinesListSupportSessionsZitadelRoleTypes.ACCEPT_VPN,
        e,
      ),
    isActionButtonsDisabled,
    isCurrentUserSessionOwner,
    isCurrentUserSessionViewer,
    isCurrentUserSessionApprover,
    isManagerOwner,
    canRequestorCancelActiveInProgress,
    canApproverCancelActiveInProgress,
    refreshSupportSession,
  };
};
